import { cn } from "~/utils/shadecn"
import { stablePartition } from "~/utils/data"

// SFW visibility policy
// ---------------------
// Two-tier gate:
//   1. SFW content is visible to all users, including anonymous visitors.
//   2. Must be payment-verified (age-verified) to see NSFW content.
//
// Network-level privacy: handled by `gateStillsToUser` in
// app/models/videos.server.ts — non-payment-verified users (anonymous or logged-in)
// receive only SFW-tagged stills.
//
// Visual blur strategy:
//   - Avatars: CSS blur-2xl only.
//   - Video stills (VideoCard): pre-blurred BlurredStill + CSS blur-2xl layered on top.
//     See workers.server.ts for server-side blur details.

export function shouldBlurAvatar(isPaymentVerified?: boolean, isSFW?: boolean) {
  return !isSFW && !isPaymentVerified
}

export function avatarImageClassName(isPaymentVerified?: boolean, isSFW?: boolean, baseClassName = "object-cover") {
  return cn(baseClassName, shouldBlurAvatar(isPaymentVerified, isSFW) && "blur-2xl")
}

export function shouldUseBlurredStill(isPaymentVerified?: boolean, sfwStills?: boolean) {
  return !sfwStills && !isPaymentVerified
}

export const videoIsSfw = (video: any) => video?.SFWStills === true
export const starIsSfw = (star: any) => star?.IsSFW === true

/**
 * Ordering counterpart to the blur rules: a viewer who is not payment-verified sees a blurred
 * placeholder for every card whose SFW flag is false, so surface the cards they can actually
 * see first. Payment-verified viewers see everything and keep the incoming order. Stable, so
 * an upstream `dailyShuffle` survives. Non-mutating; returns [] for missing input.
 *
 * `isSfw` is required on purpose: defaulting it would silently apply video semantics to stars
 * (`SFWStills` is always undefined on a star), hoisting nothing and failing quietly.
 */
export function sfwFirst<T>(
  items: T[] | null | undefined,
  isPaymentVerified: boolean | undefined,
  isSfw: (item: T) => boolean
): T[] {
  if (!items) return []
  if (isPaymentVerified) return items.slice()
  return stablePartition(items, isSfw)
}
